import {ThunkAction} from 'redux-thunk';
import {Action} from 'redux';
import {IStore} from '../interface';
import {
  fireManualEvent,
  getAssistantSession,
  getPatientAssignments,
  getPatientCareTeam,
  getPatientLibrary,
  getPatientMedications,
  getPatientRun,
  getProgramAllowedManualEvents,
  getProgramCareTeam,
  getProgramDetail,
  getProgramLibrary,
  getProgramModules,
  getProgramNext,
  getStepContent
} from '../pageAsyncActions';
import {clearErrorAction, finishLoadingAction, startLoadingAction} from '../status/actions';
import {
  getAllowedManualEventsApi,
  getPatientAssignmentsApi,
  getPatientAssistantSessionApi,
  getPatientCareTeamApi,
  getPatientLibraryApi,
  getPatientMedicationsApi,
  getPatientRunApi,
  getProgramCareTeamApi,
  getProgramDetailApi,
  getProgramLibraryApi,
  getProgramModulesApi,
  getProgramNextApi,
  getStepContentApi,
  postManualEventApi
} from '../api/patientHome';
import {
  assignmentsLoadedAction,
  assistantSessionLoadedAction,
  careTeamLoadedAction,
  libraryLoadedAction,
  medicationsLoadedAction,
  programAllowedManualEventsLoadedAction,
  programCareTeamLoadedAction,
  programDetailLoadedAction,
  programLibraryLoadedAction,
  programModulesLoadedAction,
  programNextLoadedAction,
  runLoadedAction,
  stepContentLoadedAction
} from './actions';
import {errorHandler} from '../errorHandler';
import {ToastKey} from '../../components/gritx-toast/ToastKeyEnum';
import {IProgramCareTeamMember, IProgramLibraryItem, IProgramModule, IStepContent, StepKind} from './interfaces';

export const loadPatientAssignmentsAction = (): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getPatientAssignments;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getPatientAssignmentsApi();

      dispatch(finishLoadingAction(actionKey));
      dispatch(assignmentsLoadedAction(data.assignments));
    } catch (e) {
      console.log('LoadPatientAssignmentsAction', e);
      dispatch(errorHandler({
        toastKey: ToastKey.LoadPatientAssignmentsAction,
        actionKey,
        error: e
      }));
    }
  }
});

export const loadPatientRunAction = (apId: number): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getPatientRun;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getPatientRunApi(apId);

      dispatch(finishLoadingAction(actionKey));
      dispatch(runLoadedAction({apId, pending: data.pending ?? []}));
    } catch (e) {
      console.log('LoadPatientRunAction', e);
      dispatch(errorHandler({
        toastKey: ToastKey.LoadPatientRunAction,
        actionKey,
        error: e
      }));
    }
  }
});

export const loadPatientLibraryAction = (apId: number): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getPatientLibrary;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getPatientLibraryApi(apId);

      dispatch(finishLoadingAction(actionKey));
      dispatch(libraryLoadedAction({apId, library: data}));
    } catch (e) {
      console.log('LoadPatientLibraryAction', e);
      dispatch(errorHandler({
        toastKey: ToastKey.LoadPatientLibraryAction,
        actionKey,
        error: e
      }));
    }
  }
});

export const loadPatientMedicationsAction = (): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getPatientMedications;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getPatientMedicationsApi();

      dispatch(finishLoadingAction(actionKey));
      dispatch(medicationsLoadedAction(data.medications));
    } catch (e) {
      console.log('LoadPatientMedicationsAction', e);
      dispatch(errorHandler({
        toastKey: ToastKey.LoadPatientMedicationsAction,
        actionKey,
        error: e
      }));
    }
  }
});

export const loadPatientCareTeamAction = (): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getPatientCareTeam;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getPatientCareTeamApi();

      dispatch(finishLoadingAction(actionKey));
      dispatch(careTeamLoadedAction(data.providers));
    } catch (e) {
      console.log('LoadPatientCareTeamAction', e);
      dispatch(errorHandler({
        toastKey: ToastKey.LoadPatientCareTeamAction,
        actionKey,
        error: e
      }));
    }
  }
});

export const loadProgramDetailAction = (apId: number): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getProgramDetail;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getProgramDetailApi(apId);

      dispatch(finishLoadingAction(actionKey));
      dispatch(programDetailLoadedAction({apId, detail: data}));
    } catch (e) {
      console.log('LoadProgramDetailAction', e);
      dispatch(errorHandler({toastKey: ToastKey.LoadProgramDetailAction, actionKey, error: e}));
    }
  }
});

export const loadProgramNextAction = (apId: number): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getProgramNext;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getProgramNextApi(apId);

      dispatch(finishLoadingAction(actionKey));
      dispatch(programNextLoadedAction({apId, next: data}));
    } catch (e) {
      console.log('LoadProgramNextAction', e);
      dispatch(errorHandler({toastKey: ToastKey.LoadProgramNextAction, actionKey, error: e}));
    }
  }
});

export const loadProgramModulesAction = (apId: number): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getProgramModules;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getProgramModulesApi(apId);
      const wrapped = data as unknown as {modules?: IProgramModule[]};
      const modules = Array.isArray(data) ? data : (wrapped.modules ?? []);

      dispatch(finishLoadingAction(actionKey));
      dispatch(programModulesLoadedAction({apId, modules}));
    } catch (e) {
      console.log('LoadProgramModulesAction', e);
      dispatch(errorHandler({toastKey: ToastKey.LoadProgramModulesAction, actionKey, error: e}));
    }
  }
});

export const loadProgramLibraryAction = (apId: number): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getProgramLibrary;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getProgramLibraryApi(apId);
      const wrappedLib = data as unknown as {items?: IProgramLibraryItem[]};
      const items = Array.isArray(data) ? data : (wrappedLib.items ?? []);

      dispatch(finishLoadingAction(actionKey));
      dispatch(programLibraryLoadedAction({apId, items}));
    } catch (e) {
      console.log('LoadProgramLibraryAction', e);
      dispatch(errorHandler({toastKey: ToastKey.LoadProgramLibraryAction, actionKey, error: e}));
    }
  }
});

export const loadProgramCareTeamAction = (apId: number): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getProgramCareTeam;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getProgramCareTeamApi(apId);
      const wrappedCt = data as unknown as {providers?: IProgramCareTeamMember[]; members?: IProgramCareTeamMember[]};
      const members = Array.isArray(data) ? data : (wrappedCt.providers ?? wrappedCt.members ?? []);

      dispatch(finishLoadingAction(actionKey));
      dispatch(programCareTeamLoadedAction({apId, members}));
    } catch (e) {
      console.log('LoadProgramCareTeamAction', e);
      dispatch(errorHandler({toastKey: ToastKey.LoadProgramCareTeamAction, actionKey, error: e}));
    }
  }
});

export const loadProgramAllowedManualEventsAction = (apId: number): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getProgramAllowedManualEvents;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getAllowedManualEventsApi(apId);

      dispatch(finishLoadingAction(actionKey));
      dispatch(programAllowedManualEventsLoadedAction({apId, allowed: data.allowed ?? []}));
    } catch (e) {
      console.log('LoadProgramAllowedManualEventsAction', e);
      dispatch(errorHandler({toastKey: ToastKey.LoadProgramAllowedManualEventsAction, actionKey, error: e}));
    }
  }
});

// UCSF-3546: fires a patient self-service lifecycle event (rescreen/pause/resume/reactivate).
// apId/eventCode identify the target; actorId/roleCode are always server-attributed by
// gritx-bot-app, never sent from here. onSuccess/onError let the caller (ProgramDetail) reload
// programDetail + allowed-events and clear its own pending-action UI state without this thunk
// needing to know about either.
export const fireManualEventAction = (
  apId: number, eventCode: string, reason: string | undefined, onSuccess: () => void, onError?: () => void
): ThunkAction<void, IStore, unknown, Action> => (async (dispatch) => {
  const actionKey = fireManualEvent;

  dispatch(startLoadingAction(actionKey));
  dispatch(clearErrorAction(actionKey));
  try {
    await postManualEventApi(apId, eventCode, reason);
    dispatch(finishLoadingAction(actionKey));
    onSuccess();
  } catch (e) {
    console.log('FireManualEventAction', e);
    dispatch(errorHandler({toastKey: ToastKey.FireManualEventAction, actionKey, error: e}));
    if (onError) {
      onError();
    }
  }
});

export const loadAssistantSessionAction = (): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getAssistantSession;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getPatientAssistantSessionApi();

      dispatch(finishLoadingAction(actionKey));
      dispatch(assistantSessionLoadedAction(data.active));
    } catch (e) {
      console.log('LoadAssistantSessionAction', e);
      dispatch(errorHandler({
        toastKey: ToastKey.LoadAssistantSessionAction,
        actionKey,
        error: e
      }));
    }
  }
});

export const loadStepContentAction = (
  apId: number, stepId: number, stepKind?: StepKind
): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getStepContent;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      const {data} = await getStepContentApi(apId, stepId, stepKind);
      const wrapped = data as unknown as {step?: IStepContent};
      const stepContent: IStepContent = (wrapped.step ?? data) as IStepContent;

      dispatch(finishLoadingAction(actionKey));
      dispatch(stepContentLoadedAction({apId, stepId, stepContent}));
    } catch (e) {
      console.log('LoadStepContentAction', e);
      dispatch(errorHandler({toastKey: ToastKey.LoadStepContentAction, actionKey, error: e}));
    }
  }
});
